import { ApiRecordType } from '../../../../core/record-types/entities/api-record-type.entity';
import { ApiTranslation } from '../../../api/entities/translation.entity';
import { ValidationResponse } from '../../../api/entities/validation-response.entity';
import { ApiAction } from '../../../common/entities/actions/api-action.entity';
import { DottedKey } from '../../../field-definitions/entities/field-definition.entity';
import { Task } from '../../../tasks/entities/task.entity';
import { Zone } from '../../../zones/entities/zone.entity';

export enum RecordType {
    Record = 'Record',
    Media = 'Media',
    Item = 'Item',
    Newspaper = 'Newspaper',
    NewspaperPage = 'NewspaperPage',
    Sip = 'Sip',
    Mh2Collection = 'Mh2Collection',
    PersonalSelection = 'Selection.Personal',
    GenericSelection = 'Selection.Generic',
    Classification = 'Classification',
    ThesaurusSuggestion = 'ThesaurusSuggestion',
    UserSuggestion = 'UserSuggestion',
    Representation = 'Representation',
    Series = 'Series',
    Dossier = 'Dossier',
    Document = 'Document',
    SeriesTemplate = 'Series.Template',
    PackagingUnit = 'PackagingUnit',
    PackagingType = 'PackagingType',
    TransportUnit = 'TransportUnit',
    TransportType = 'TransportType',
    Transport = 'Transport',
    BulkSeries = 'Bulk.Series',
    OrphanDocument = 'Document.Orphaned',
    LocationElement = 'LocationElement',
    DepotRequest = 'DepotRequest',
    SipData = 'SipData',
    Selection = 'Selection'
}

export enum RecordTypeStructure {
    Classification = 'Classification',
    Data = 'Data',
    DataFlat = 'DataFlat',
    Intellectual = 'Intellectual',
    Representation = 'Representation'
}

export interface Metadata {
    Administrative: AdministrativeMetadata;
    Context: ContextMetadata;
    Descriptive: DescriptiveMetadata;
    Dynamic: DynamicMetadata;
    Internal: InternalMetadata;
    MergeStrategies?: MergeStrategies;
    RecordInformation: RecordInformationMetaData;
    RightsManagement: RightsManagementMetadata;
    Structural: StructuralMetadata;
    Technical: TechnicalMetadata;
    Timers: TimersMetadata;
    Workflow: WorkflowMetadata;
    _links?: ApiAction[];
    [key: string]: any;
}

export enum MediaType {
    image = 'image',
    video = 'video',
    videofragment = 'videofragment',
    audio = 'audio',
    audiofragment = 'audiofragment',
    document = 'document',
    page = 'page',
    newspaper = 'newspaper',
    newspaperpage = 'newspaperpage',
    article = 'article',
    layer = 'layer',
    filearchive = 'filearchive',
    metadataonly = 'metadataonly',
    set = 'set',
    collection = 'collection',
    processing = 'processing'
}

/* temporary hack */
export enum MH2Types {
    Mh2Collection = 'Mh2Collection',
    Fragment = 'fragment'
}

export type MH2MediaType = MediaType | MH2Types;

export type UUID = string;

enum FragmentIdBrand {
    _ = ''
}
export type FragmentId = string & FragmentIdBrand;

enum MediaObjectIdBrand {
    _ = ''
}
export type MediaObjectId = string & MediaObjectIdBrand;

enum RecordIdBrand {
    _ = ''
}
export type RecordId = string & RecordIdBrand;

export type Identifier = FragmentId | RecordId | MediaObjectId;

enum MediaObjectIdWithNumberBrand {
    _ = ''
}
export type MediaObjectIdWithNumber = string & MediaObjectIdWithNumberBrand;

enum Md5Brand {
    _ = ''
}
export type Md5 = string & Md5Brand;

export enum OriginalStatus {
    in_progress = 'in_progress',
    completed = 'completed',
    failed = 'failed'
}
export enum RecordStatus {
    Uploaded = 'Uploaded',
    Uploading = 'Uploading',
    Processing = 'Processing',
    New = 'New',
    DraftValid = 'Draft.Valid',
    DraftInvalid = 'Draft.Invalid',
    Submitted = 'Submitted',
    Rejected = 'Rejected',
    RejectedForCorrection = 'RejectedForCorrection',
    Deleted = 'Deleted',
    Published = 'Published',
    Destructed = 'Destructed',
    ApprovedForDestruction = 'ApprovedForDestruction',
    ApprovedForTransfer = 'ApprovedForTransfer',
    Transferred = 'Transferred',
    Archived = 'Archived'
}
export enum BrowseStatus {
    in_progress = 'in_progress',
    completed = 'completed',
    cancelled = 'cancelled',
    failed = 'failed',
    no_browse = 'no_browse'
}
export enum ArchiveStatus {
    in_progress = 'in_progress',
    on_disk = 'on_disk',
    failed = 'failed'
}

export enum DeleteStatus {
    NotDeleted = 'NotDeleted',
    LogicallyDeleted = 'LogicallyDeleted',
    PermanentlyDeleted = 'PermanentlyDeleted'
}

export enum VersioningStatus {
    Head = 'Head',
    Tail = 'Tail',
    Draft = 'Draft',
    Rejected = 'Rejected'
}

export type TimeCode = string;

export enum TernaryLogic {
    true = '1',
    false = '0',
    both = '*'
}

export interface Keywords {
    Keyword: string[];
}
export interface Categories {
    Category: string[];
}

export enum Family {
    Descriptive = 'Descriptive',
    Dynamic = 'Dynamic',
    Administrative = 'Administrative',
    Technical = 'Technical',
    Internal = 'Internal',
    Structural = 'Structural',
    RightsManagement = 'RightsManagement',
    Context = 'Context',
    MergeStrategies = 'MergeStrategies',
    Workflow = 'Workflow',
    RecordInformation = 'RecordInformation',
    Timers = 'Timers'
}

export interface DescriptiveMetadata {
    Title: string;
    OriginalFilename: string;
    UploadedBy: string | null;
    KeyframeStart: number;
    Description: string | null;
    CreationDate: string | null;
    RightsOwner: string | null;
    Rights: string | null;
    Keywords: Keywords;
    Categories: Categories;
    Publisher: string | null;
    Authors: Record<string, string[]>;
    NonPreferredTerm: string | null;
    Publications: string | null;
    Location: string | null; // Deprecated: Use PositionField
    Address: Record<string, any>; // Deprecated: Use PositionField
    Position: Record<string, any>;
}

export interface DynamicMetadata {
    [key: string]: any;
    Mh2Gdpr: string;
    Organiser: string;
    Mh2Reuse: {
        Destination: string[];
    };
    Mh2AiKeywords: {
        Mh2AiKeyword: string[];
    };
    Mh2InvalidFields: {
        Mh2DottedKey: string[];
    };
}

export interface AdministrativeMetadata {
    readonly OrganisationName: string | null;
    readonly LastModifiedDate: string;
    readonly PublicationDate: string | null;
    readonly LogicalDeletionDate: string | null;
    readonly ArchiveDate: string;
    readonly Type: MediaType;
    readonly IsSynchronized: boolean;
    readonly ExternalId: string | null;
    readonly DepartmentName: string | null;
    readonly IngestTape: string | null;
    readonly RecordType: RecordType;
    readonly MainRecordType: RecordType;
    readonly RecordStatus: RecordStatus;
    readonly DeleteStatus: DeleteStatus;
    readonly HasFailedJobs: boolean;
    readonly IsOriginal: boolean;
    readonly IsAccess: boolean;
    readonly IsPreservation: boolean;
    readonly RecordRejections?: {
        Rejection: Rejection[];
    };
}

export interface TechnicalMetadata {
    readonly FileSize: number;
    readonly PronomId: string | null;
    readonly OriginalExtension: string | null;
    readonly Md5: Md5 | null;
    readonly MimeType: string | null;
    readonly Width: number | null;
    readonly Height: number | null;
    readonly ImageSize: string | null;
    readonly ImageOrientation: string | null;
    readonly ImageQuality: string | null;
    readonly VideoTechnical: any | null;
    readonly AudioTechnical: any | null;
    readonly VideoFormat: any | null;
    readonly DurationTimeCode: TimeCode | null;
    readonly StartTimeCode: TimeCode | null;
    readonly EndTimeCode: TimeCode | null;
    readonly DurationFrames: number | null;
    readonly StartFrames: number | null;
    readonly EndFrames: number | null;
    readonly VideoCodec: string | null;
    readonly VideoFps: any | null;
    readonly VideoBitRate: any | null;
    readonly BitRate: any | null;
    readonly Exif: Record<string, string>;
}

export interface InternalMetadata {
    readonly MediaObjectId: MediaObjectId;
    readonly FragmentId: FragmentId;
    readonly RecordId: RecordId;
    readonly OriginalStatus: OriginalStatus;
    readonly BrowseStatus: BrowseStatus;
    readonly ArchiveStatus: ArchiveStatus;
    readonly UploadedById: string | null;
    readonly OrganisationId: string;
    readonly IngestSpaceId: string | null;
    readonly IsInIngestSpace: boolean;
    readonly IsFragment: boolean;
    readonly HasKeyframes: boolean;
    readonly ContainsGeoData: boolean;
    readonly PathToKeyframe: string;
    readonly PathToKeyframeThumb: string;
    readonly PathToOriginal: string;
    readonly DepartmentId: string | null;
    readonly Browses: any | null;
    readonly PathToVideo: string | null;
    readonly Profiles: any;
    readonly PathToPreview: string | null;
}

export interface StructuralMetadata {
    Relations: Record<string, FragmentId[] | null>;
    Sets: { Set: MediaObjectIdWithNumber[] }; // legacy
    Collections: { Collection: MediaObjectIdWithNumber[] }; // legacy
    Newspapers: { Newspaper: FragmentId[] }; // legacy
    MainFragment: FragmentId | null;
    Fragments: { Fragment: FragmentId[] };
    FragmentStartFrames: number;
    FragmentEndFrames: number;
    FragmentDurationFrames: number | null;
    FragmentDurationTimeCode: TimeCode | null;
    FragmentStartTimeCode: TimeCode | null;
    FragmentEndTimeCode: TimeCode | null;
    FieldDefinition: FieldDefinitionStub;
    ReferenceCodes: ReferenceCodes;
    ReferenceTitles: ReferenceTitles;
    ParentRecordId: RecordId | null;
    PreviewRecordId: RecordId | null;
    HasChildren: boolean;
    HasNonRepresentationChildren: boolean;
    RecordStructure: string;
    OriginalRecordId: RecordId | null;
    Versioning: {
        Id: string;
        Version: number;
        Status: VersioningStatus;
    };
}

export interface FieldDefinitionStub {
    DottedKey: DottedKey;
    LongTranslations: {
        [key: string]: string;
    };
}

export type ReferenceCodes = {
    [recordType in RecordType]?: MediaObjectId[];
};

export type ReferenceTitles = {
    [recordType in RecordType]?: string[];
};

export interface RightsManagementMetadata {
    readonly Permissions: Permissions;
    readonly ExpiryDate: string | null;
    readonly ExpiryStatus: string | null;
    readonly Zone: Zone;
    readonly RecordPhase: RecordPhase;
}

export interface Permissions {
    readonly Read: string[];
    readonly Write: string[];
    readonly Export: string[];
}

export enum RecordPhase {
    Archived = 'Archived',
    Concept = 'Concept',
    Published = 'Published'
}

export interface ContextMetadata {
    readonly IsEditable: boolean;
    readonly HasEditableStatus: boolean;
    readonly IsDeletable: boolean;
    readonly IsPublic: boolean;
    readonly IsExportable: boolean;
    readonly IsPublishable: boolean;
    readonly IsSubmittable: boolean;
    readonly HasSubmittableStatus: boolean;
    readonly Validation: ValidationResponse | null;
    readonly Profiles: string[] | null;
    readonly AllowedChildren: ApiRecordType[];
    readonly Suggestions: SuggestionMetadata[];
}
export interface SuggestionMetadata {
    readonly Descriptive: {
        readonly Title: string;
    };
    readonly Structural: {
        readonly FieldDefinition: {
            readonly DottedKey: DottedKey;
        };
    };
    readonly Internal: {
        readonly FragmentId: FragmentId;
    };
}

export interface WorkflowMetadata {
    readonly Tasks: Tasks;
}

export interface RecordInformationMetaData {
    readonly UserContact: UserContact;
    readonly PreferredLabels: PreferredLabelMetadata;
    readonly AlternativeLabels: AlternativeLabelMetadata;
    readonly Classification?: ClassificationMetadata;
}

export interface UserContact {
    Email: string;
    FirstName: string;
    LastName: string;
    PasswordHash?: string;
}

export interface PreferredLabelMetadata {
    PreferredLabel: ThesaurusApiTranslation[];
}

export interface AlternativeLabelMetadata {
    AlternativeLabel: ThesaurusApiTranslation[];
}

export interface ClassificationMetadata {
    BatchId: string;
    HasChanges: boolean;
}

export interface ThesaurusApiTranslation {
    Lang: string;
    Label: string;
}

export interface Tasks {
    Task: Task[];
}

export type MergeStrategies = Record<string, MergeStrategy>;

export enum MergeStrategy {
    'OVERWRITE' = 'OVERWRITE',
    'KEEP' = 'KEEP',
    'MERGE' = 'MERGE',
    'SUBTRACT' = 'SUBTRACT'
}

export enum PreviewStatus {
    NoPreview,
    Processing,
    Error,
    Ready
}

export interface TimersMetadata {
    readonly Destruction: TimerDates;
}

export interface TimerDates {
    readonly EffectiveDate: string | null;
    readonly ExpectedDate: string | null;
}

export interface RejectionList {
    RecordId: string;
    Breadcrumb: string;
    Rejections: Rejection[];
}

export interface Rejection {
    Reason: string;
    Motivation?: string;
    Description: string;
    Descriptions: ApiTranslation[];
    Date: string;
    User: string;
    Row?: number;
    Path?: string;
}
